'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import type { ISuccessStoryTestimonial } from '@/interfaces';
import { getThemeBackgroundClass, getThemeTextClass } from '@/lib/theme';
import type { Language } from '@/lib/translations';
import type { ITheme } from '@/types/theme';

/** Five filled stars, in the page's own accent. */
function Stars({ accent }: { accent: string }) {
  return (
    <span aria-hidden="true" className={`mb-4 flex gap-0.5 ${accent}`}>
      {[0, 1, 2, 3, 4].map(index => (
        <svg key={index} viewBox="0 0 20 20" className="h-4 w-4 fill-current">
          <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

/**
 * What other shops say, as a row of cards you can push along. Each card
 * links to that shop's own case page.
 */
export default function SuccessStoryTestimonials({
  testimonials,
  language,
  theme = null,
}: {
  testimonials: ISuccessStoryTestimonial[];
  language: Language;
  theme?: ITheme;
}) {
  const en = language === 'en';
  const accent = getThemeTextClass(theme);
  const railRef = useRef<HTMLDivElement>(null);

  if (testimonials.length === 0) return null;

  const push = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * (rail.clientWidth * 0.8),
      behavior: 'smooth',
    });
  };

  return (
    <section className={`${getThemeBackgroundClass(theme, 'secondary')} py-16`}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              {en ? 'What other shops say' : '其他店家也這麼說'}
            </h2>
            <p className="escape text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {en
                ? 'Studios, clinics and salons already running on Omcean Booking.'
                : '已經用 Omcean Booking 經營日常的教室、診所與店家。'}
            </p>
          </div>

          <div className="hidden gap-2 sm:flex">
            {([-1, 1] as const).map(direction => (
              <button
                key={direction}
                type="button"
                onClick={() => push(direction)}
                aria-label={
                  direction === -1
                    ? en
                      ? 'Previous'
                      : '上一則'
                    : en
                      ? 'Next'
                      : '下一則'
                }
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:text-white"
              >
                <span aria-hidden="true">{direction === -1 ? '←' : '→'}</span>
              </button>
            ))}
          </div>
        </div>

        <div
          ref={railRef}
          className="-mx-4 flex snap-x snap-mandatory scroll-pl-4 gap-5 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:scroll-pl-0 sm:px-0"
        >
          {testimonials.map(item => (
            <Link
              key={item.slug}
              href={item.href}
              className="group w-[78vw] max-w-[340px] shrink-0 snap-start rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gray-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 sm:w-[340px] sm:max-w-none"
            >
              <Stars accent={accent} />
              <p className="escape mb-6 line-clamp-5 text-base leading-relaxed text-gray-700 dark:text-gray-200">
                {item.quote}
              </p>
              <span className="flex items-center gap-3">
                <Image
                  src={item.photo}
                  alt={item.name}
                  width={96}
                  height={96}
                  className="h-11 w-11 rounded-full object-cover"
                />
                <span className="block min-w-0">
                  <span className="escape block truncate text-sm font-bold text-gray-900 dark:text-white">
                    {item.name}
                  </span>
                  <span className="escape block truncate text-xs text-gray-500 dark:text-gray-400">
                    {item.role}
                  </span>
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
